import React, { useEffect, useState } from 'react';
import { getStreamInfo, logout, useApp } from '../store';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { AudioPlayer } from './AudioPlayer';
import { Warning } from './Warning';
import { Timer } from './Timer';
import { AudioFailed } from './AudioFailed';

declare global {
  interface Window {
    chrome: typeof chrome;
    cast: typeof cast;
  }
}

export const Stream: React.FC = () => {
  const { t } = useTranslation();
  const [play, setPlay] = useState(false);
  const [fail, setFail] = useState(false);
  const [warning, setWarning] = useState<string[]>([]);
  const [timer, setTimer] = useState(0);
  const [audioRef, setAudioRef] = useState<HTMLAudioElement | null>(null);
  const [castConnected, setCastConnected] = useState(false);
  const [castSession, setCastSession] = useState<cast.framework.CastSession | null>(null);

  const { state, dispatch } = useApp();

  useEffect(() => {
    getStreamInfo(dispatch);
  }, [dispatch]);

  // Initialize Chromecast
  useEffect(() => {
    const initializeCast = () => {
      if (window.cast && window.cast.framework) {
        const context = window.cast.framework.CastContext.getInstance();
        context.setOptions({
          receiverApplicationId: window.chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
          autoJoinPolicy: window.chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
        });

        context.addEventListener(
          window.cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
          (event) => {
            switch (event.sessionState) {
              case window.cast.framework.SessionState.SESSION_STARTED:
              case window.cast.framework.SessionState.SESSION_RESUMED:
                setCastConnected(true);
                setCastSession(context.getCurrentSession());
                break;
              case window.cast.framework.SessionState.SESSION_ENDED:
                setCastConnected(false);
                setCastSession(null);
                break;
            }
          }
        );
      }
    };

    // Wait for cast framework to load
    if (window['__onGCastApiAvailable']) {
      initializeCast();
    } else {
      window['__onGCastApiAvailable'] = (isAvailable: boolean) => {
        if (isAvailable) {
          initializeCast();
        }
      };
    }
  }, []);

  useEffect(() => {
    if (play) {
      getStreamInfo(dispatch);
      setFail(false);
      setWarning([]);
      setTimer(0);

      if (castConnected && castSession) {
        castStream();
      }
    }
  }, [play, dispatch]);

  // Cast when connection is established while playing
  useEffect(() => {
    if (play && castConnected && castSession) {
      castStream();
    }
  }, [castConnected, castSession]);

  const castStream = async () => {
    if (!castSession) return;

    try {
      const response = await fetch('/api/token');
      const token = await response.json();

      const mediaInfo = new window.chrome.cast.media.MediaInfo(
        `${window.location.protocol}//${window.location.host}/api/stream.mp3?token=${token}`,
        'audio/mpeg'
      );

      mediaInfo.streamType = window.chrome.cast.media.StreamType.LIVE;

      const metadata = new window.chrome.cast.media.GenericMediaMetadata();
      metadata.title = 'Samlingstelefonen';
      mediaInfo.metadata = metadata;

      const request = new window.chrome.cast.media.LoadRequest(mediaInfo);

      castSession.loadMedia(request).then(
        () => console.log('Cast media loaded'),
        (error) => console.error('Cast media error', error)
      );
    } catch (error) {
      console.error('Failed to cast stream', error);
    }
  };

  const onFailure = () => {
    Sentry.captureException('Audio Failed, User is notified');
    setFail(true);
  };

  const audioFailedConfirmed = () => {
    Sentry.captureException('Audio Failed, User confirmed');
    setFail(false);
    setPlay(false);
    setTimer(0);
    setPlay(true);
  };

  const audioFailedClosed = () => {
    Sentry.captureException('Audio Failed, User declined');
    setFail(false);
  };

  const handlePlay = () => {
    console.log('User pressed play');
    setPlay(true);
  };

  const handleStop = () => {
    console.log('User pressed stop');

    if (castConnected && castSession) {
      console.log('Stopping chromecast');
      const mediaSession = castSession.getMediaSession();
      if (mediaSession) {
        mediaSession.stop(new window.chrome.cast.media.StopRequest(), () => {
          console.log('Chromecast stopped');
        }, (error) => {
          console.error('Error stopping chromecast', error);
        });
      }
    }

    setPlay(false);
  };

  const handleLogout = () => {
    console.log('User logged out');
    logout(dispatch);
  };

  const handleCastClick = () => {
    if (window.cast && window.cast.framework) {
      const context = window.cast.framework.CastContext.getInstance();
      context.requestSession().then(
        () => console.log('Cast session started'),
        (error) => console.error('Cast session error', error)
      );
    }
  };

  return (
    <>
      <div className="logout_menu">
        <button className="logout" onClick={handleLogout}>
          &#10005;
        </button>
      </div>
      {fail && <AudioFailed onConfirm={audioFailedConfirmed} onClose={audioFailedClosed} />}
      <Warning message={warning} />
      <p>{t('streamText')}</p>
      <div className={'title'}>
        <h1>{state.transferConference || state.conference}</h1>
        <h2>{play ? <Timer timestamp={timer} /> : <br />}</h2>
      </div>

      <audio ref={(c) => setAudioRef(c)} />
      {!castConnected && play && (
        <AudioPlayer
          player={audioRef}
          onFailure={onFailure}
          onPlayTimerUpdate={(timestamp) => setTimer(timestamp)}
        />
      )}
      {state.loading !== true ? (
        play === true ? (
          <button className={'primary'} onClick={handleStop}>
            ■ {t('streamStopButton')}
          </button>
        ) : (
          <button className={'primary'} onClick={handlePlay}>
            ▶ {t('streamStartButton')}
          </button>
        )
      ) : (
        <button className={'primary'} disabled>
          Loading...
        </button>
      )}
      <div className={'bottom'}>
        {window.cast && window.cast.framework && (
          <button className={'link-button'} onClick={handleCastClick}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="24"
              viewBox="0 0 24 24"
              width="24"
              fill={castConnected ? '#1976d2' : 'currentColor'}
              style={{ verticalAlign: 'middle', marginRight: '8px' }}
            >
              <path d="M0 0h24v24H0z" fill="none"/>
              <path d="M1 18v3h3c0-1.66-1.34-3-3-3zm0-4v2c2.76 0 5 2.24 5 5h2c0-3.87-3.13-7-7-7zm0-4v2c4.97 0 9 4.03 9 9h2c0-6.08-4.93-11-11-11zm20-7H3c-1.1 0-2 .9-2 2v3h2V5h18v14h-7v2h7c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/>
            </svg>
            {castConnected ? 'Connected to Chromecast' : 'Cast'}
          </button>
        )}
      </div>
    </>
  );
};
